import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { NotificationProvider } from "@/lib/notifications";
import { Toaster } from "@/components/ui/sonner";
import ProtectedRoute from "@/components/ProtectedRoute";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import StudentDashboard from "@/pages/student/Dashboard";
import ExamList from "@/pages/student/ExamList";
import ExamTake from "@/pages/student/ExamTake";
import Results from "@/pages/student/Results";
import Analytics from "@/pages/student/Analytics";
import AttemptDetail from "@/pages/student/AttemptDetail";
import StudentMaterials from "@/pages/student/Materials";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import TeacherDashboard from "@/pages/teacher/Dashboard";
import TeacherExamList from "@/pages/teacher/ExamList";
import ExamEditor from "@/pages/teacher/ExamEditor";
import TeacherClasses from "@/pages/teacher/Classes";
import TeacherAttempts from "@/pages/teacher/Attempts";
import TeacherAttemptDetail from "@/pages/teacher/AttemptDetail";
import TeacherMaterials from "@/pages/teacher/Materials";
import QuestionBank from "@/pages/teacher/QuestionBank";
import AiTextbook from "@/pages/teacher/AiTextbook";
import AdminDashboard from "@/pages/admin/Dashboard";
import AdminUsers from "@/pages/admin/Users";
import AdminInstitutions from "@/pages/admin/Institutions";
import AdminSubjects from "@/pages/admin/Subjects";
import AdminActivityLog from "@/pages/admin/ActivityLog";
import AdminSettings from "@/pages/admin/Settings";
import AdminAnnouncements from "@/pages/admin/Announcements";
import AdminBulkEmail from "@/pages/admin/BulkEmail";
import TextbookManager from "@/pages/shared/TextbookManager";
import Library from "@/pages/student/Library";

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Landing />;
  const target =
    user.role === "admin" ? "/admin" : user.role === "teacher" ? "/teacher" : "/dashboard";
  return <Navigate to={target} replace />;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <NotificationProvider>
          <Toaster position="top-right" richColors />
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />

            {/* Student */}
            <Route path="/dashboard" element={<ProtectedRoute roles={["student"]}><StudentDashboard /></ProtectedRoute>} />
            <Route path="/dashboard/exams" element={<ProtectedRoute roles={["student"]}><ExamList /></ProtectedRoute>} />
            <Route path="/dashboard/results" element={<ProtectedRoute roles={["student"]}><Results /></ProtectedRoute>} />
            <Route path="/dashboard/analytics" element={<ProtectedRoute roles={["student"]}><Analytics /></ProtectedRoute>} />
            <Route path="/dashboard/attempts/:attemptId" element={<ProtectedRoute roles={["student"]}><AttemptDetail /></ProtectedRoute>} />
            <Route path="/dashboard/materials" element={<ProtectedRoute roles={["student"]}><StudentMaterials /></ProtectedRoute>} />
            <Route path="/dashboard/library" element={<ProtectedRoute roles={["student"]}><Library /></ProtectedRoute>} />
            <Route path="/exam/:examId" element={<ProtectedRoute roles={["student"]}><ExamTake /></ProtectedRoute>} />

            {/* Teacher */}
            <Route path="/teacher" element={<ProtectedRoute roles={["teacher", "admin"]}><TeacherDashboard /></ProtectedRoute>} />
            <Route path="/teacher/exams" element={<ProtectedRoute roles={["teacher", "admin"]}><TeacherExamList /></ProtectedRoute>} />
            <Route path="/teacher/exams/new" element={<ProtectedRoute roles={["teacher", "admin"]}><ExamEditor /></ProtectedRoute>} />
            <Route path="/teacher/exams/:examId" element={<ProtectedRoute roles={["teacher", "admin"]}><ExamEditor /></ProtectedRoute>} />
            <Route path="/teacher/classes" element={<ProtectedRoute roles={["teacher", "admin"]}><TeacherClasses /></ProtectedRoute>} />
            <Route path="/teacher/attempts" element={<ProtectedRoute roles={["teacher", "admin"]}><TeacherAttempts /></ProtectedRoute>} />
            <Route path="/teacher/attempts/:attemptId" element={<ProtectedRoute roles={["teacher", "admin"]}><TeacherAttemptDetail /></ProtectedRoute>} />
            <Route path="/teacher/materials" element={<ProtectedRoute roles={["teacher", "admin"]}><TeacherMaterials /></ProtectedRoute>} />
            <Route path="/teacher/textbooks" element={<ProtectedRoute roles={["teacher", "admin"]}><TextbookManager /></ProtectedRoute>} />
            <Route path="/teacher/question-bank" element={<ProtectedRoute roles={["teacher", "admin"]}><QuestionBank /></ProtectedRoute>} />
            <Route path="/teacher/ai-textbook" element={<ProtectedRoute roles={["teacher", "admin"]}><AiTextbook /></ProtectedRoute>} />

            {/* Admin */}
            <Route path="/admin" element={<ProtectedRoute roles={["admin"]}><AdminDashboard /></ProtectedRoute>} />
            <Route path="/admin/users" element={<ProtectedRoute roles={["admin"]}><AdminUsers /></ProtectedRoute>} />
            <Route path="/admin/institutions" element={<ProtectedRoute roles={["admin"]}><AdminInstitutions /></ProtectedRoute>} />
            <Route path="/admin/exams" element={<ProtectedRoute roles={["admin"]}><TeacherExamList /></ProtectedRoute>} />
            <Route path="/admin/materials" element={<ProtectedRoute roles={["admin"]}><TeacherMaterials /></ProtectedRoute>} />
            <Route path="/admin/textbooks" element={<ProtectedRoute roles={["admin"]}><TextbookManager /></ProtectedRoute>} />
            <Route path="/admin/question-bank" element={<ProtectedRoute roles={["admin"]}><QuestionBank /></ProtectedRoute>} />
            <Route path="/admin/subjects" element={<ProtectedRoute roles={["admin"]}><AdminSubjects /></ProtectedRoute>} />
            <Route path="/admin/activity" element={<ProtectedRoute roles={["admin"]}><AdminActivityLog /></ProtectedRoute>} />
            <Route path="/admin/announcements" element={<ProtectedRoute roles={["admin"]}><AdminAnnouncements /></ProtectedRoute>} />
            <Route path="/admin/bulk-email" element={<ProtectedRoute roles={["admin"]}><AdminBulkEmail /></ProtectedRoute>} />
            <Route path="/admin/settings" element={<ProtectedRoute roles={["admin"]}><AdminSettings /></ProtectedRoute>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </NotificationProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
